import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { QcLot } from '@alpha-edge/shared'
import { useApiList } from '../../hooks/use-api-list'
import { useDebounce } from '../../hooks/use-debounce'
import {
  PageHeader,
  DataTable,
  Pagination,
  StatusBadge,
  ColumnSettingsButton,
  btnPrimary,
  input,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { useTableColumns } from '../../hooks/use-table-columns'

const columns: readonly Column<QcLot>[] = [
  { key: 'lot', header: 'Lot #', sortable: true, filterable: true, render: (l) => <strong>{l.lotNumber}</strong> },
  { key: 'testType', header: 'Test Type', sortable: true, filterable: true, render: (l) => l.testType },
  { key: 'analyte', header: 'Analyte', sortable: true, filterable: true, render: (l) => l.analyte },
  { key: 'level', header: 'Level', sortable: true, render: (l) => <StatusBadge status={l.level} /> },
  {
    key: 'target',
    header: 'Target',
    sortable: true,
    render: (l) => `${l.targetValue} +/- ${l.targetSd}${l.unit ? ` ${l.unit}` : ''}`,
    sortValue: (l) => l.targetValue,
  },
  { key: 'status', header: 'Status', sortable: true, render: (l) => <StatusBadge status={l.status} /> },
  {
    key: 'expiry',
    header: 'Expires',
    sortable: true,
    render: (l) => (l.expirationDate ? new Date(l.expirationDate).toLocaleDateString() : '--'),
    sortValue: (l) => (l.expirationDate ? new Date(l.expirationDate).getTime() : 0),
  },
]

export function QcLotListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)
  const { data, total, page, loading, setPage } = useApiList<QcLot>({
    path: '/qc/lots',
    filters: { search: debouncedSearch || undefined },
  })
  const { tableId: _, ...tableColumnProps } = useTableColumns('qc-lots')

  return (
    <div>
      <PageHeader
        title="QC Lots"
        subtitle={`${total} total`}
        actions={
          <>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button onClick={() => navigate('/qc/lots/new')} style={btnPrimary}>
              Add QC Lot
            </button>
          </>
        }
      />
      <div style={{ marginBottom: '1rem' }}>
        <input
          style={{ ...input, width: '300px' }}
          placeholder="Search by lot number..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={data}
            keyField="id"
            onRowClick={(l) => navigate(`/qc/lots/${l.id}`)}
            emptyMessage="No QC lots found."
            {...tableColumnProps}
          />
          <Pagination page={page} total={total} limit={25} onPageChange={setPage} />
        </>
      )}
    </div>
  )
}
